import { useEffect, useMemo, useState } from 'react';
import { AccountTreeOutlined } from '@mui/icons-material';
import { Button, Typography } from '@mui/material';
import { GlobalSearch, IdentityAvatar, InlineNotice, type GlobalSearchGroup } from '../../design-system';
import { heydayPageIcons } from '../../config/heydayPageIcons';
import { canLoadHeydayPage, type HeydayPage } from '../../config/heydayPageManifest';
import { subscribeToHeydayLocation } from '../routing';
import { fixtureEmployeeDirectory, type EmployeeDirectoryPort } from '../../features/people/public';
import { buildDirectorySearchIndex, buildTaskCandidates, searchDirectoryCandidates, searchTaskCandidates, type SearchCandidate, type SearchCapabilityContext } from './searchModel';
import { useSearchDirectory } from './useSearchDirectory';

export type HeaderSearchProps = {
  capability: SearchCapabilityContext;
  visiblePages: ReadonlySet<HeydayPage>;
  mode: 'employee' | 'admin';
  directoryPort: EmployeeDirectoryPort;
  fixtureMode: boolean;
  enabled: boolean;
  onNavigate: (page: HeydayPage, query?: string) => boolean | void;
};

/** App은 tenant·effective principal·권한이 달라질 때 key를 바꿔 검색과 메모리 캐시를 함께 폐기합니다. */
export function HeaderSearch({ capability, visiblePages, mode, directoryPort, fixtureMode, enabled, onNavigate }: HeaderSearchProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [showAllTasks, setShowAllTasks] = useState(false);
  const canSearchDirectory = visiblePages.has('directory') && canLoadHeydayPage('directory', capability);
  const directory = useSearchDirectory(open && enabled, canSearchDirectory, () => fixtureMode
    ? Promise.resolve(fixtureEmployeeDirectory)
    : directoryPort.loadDirectory());
  const tasks = useMemo(() => buildTaskCandidates({ currentMode: mode, visiblePages, capability }), [mode, visiblePages, capability]);
  const taskResults = useMemo(() => searchTaskCandidates(tasks, query, { currentMode: mode }), [tasks, query, mode]);
  const index = useMemo(() => directory.data ? buildDirectorySearchIndex(directory.data) : undefined, [directory.data]);
  const matches = useMemo(() => index && query.trim() ? searchDirectoryCandidates(index, query) : undefined, [index, query]);

  const close = () => { setOpen(false); setQuery(''); setShowAllTasks(false); };
  useEffect(() => subscribeToHeydayLocation(() => {
    setOpen(false); setQuery(''); setShowAllTasks(false);
  }), []);
  useEffect(() => { if (!enabled) { setOpen(false); setQuery(''); setShowAllTasks(false); } }, [enabled]);

  const navigate = (page: HeydayPage, parameters?: URLSearchParams | string) => {
    if (onNavigate(page, parameters?.toString()) === false) return false;
    close();
    return true;
  };
  const candidates = [
    ...taskResults,
    ...(canSearchDirectory ? matches?.people ?? [] : []),
    ...(canSearchDirectory ? matches?.organizations ?? [] : []),
  ];
  const select = (id: string) => {
    const candidate = candidates.find((item) => item.id === id);
    if (!candidate) return false;
    const target = candidate.target;
    if (target.kind === 'page') return navigate(target.page, target.query);
    if (target.kind === 'worker') return navigate('directory', new URLSearchParams({ worker: target.workerId }));
    return navigate('directory', new URLSearchParams({ view: 'chart', organization: target.organizationId }));
  };
  const moreDirectory = (view: 'directory' | 'chart') => {
    const parameters = new URLSearchParams({ search: query.trim() });
    if (view === 'chart') parameters.set('view', view);
    navigate('directory', parameters);
  };
  const item = (candidate: SearchCandidate) => ({
    id: candidate.id,
    label: candidate.label,
    description: candidate.description,
    leading: candidate.category === 'people'
      ? <IdentityAvatar size={32} src={candidate.avatarUrl} alt="" aria-hidden>{Array.from(candidate.label)[0]}</IdentityAvatar>
      : candidate.page ? heydayPageIcons[candidate.page] : <AccountTreeOutlined />,
  });
  const groups: GlobalSearchGroup[] = [{
    id: 'tasks', label: query.trim() ? '업무·메뉴' : '바로가기',
    items: (showAllTasks ? taskResults : taskResults.slice(0, 4)).map(item),
    ...(!showAllTasks && taskResults.length > 4
      ? { moreLabel: `업무·메뉴 더 보기 (${taskResults.length})`, onMore: () => setShowAllTasks(true) } : {}),
  }];
  if (query.trim() && canSearchDirectory && matches) {
    groups.push({
      id: 'people', label: '사람', items: matches.people.slice(0, 4).map(item),
      ...(matches.peopleTotal > 4
        ? { moreLabel: `디렉터리에서 더 보기 (${matches.peopleTotal})`, onMore: () => moreDirectory('directory') } : {}),
    });
    groups.push({
      id: 'organizations', label: '조직', items: matches.organizations.slice(0, 4).map(item),
      ...(matches.organizationsTotal > 4
        ? { moreLabel: `조직도에서 더 보기 (${matches.organizationsTotal})`, onMore: () => moreDirectory('chart') } : {}),
    });
  }
  const shownCount = groups.reduce((total, group) => total + group.items.length, 0);
  const loading = canSearchDirectory && directory.loading;
  const error = canSearchDirectory && directory.error;
  const status = error
    ? <InlineNotice severity="error" density="compact" action={<Button size="small" color="inherit" onClick={directory.retry}>다시 시도</Button>}>사람·조직을 불러오지 못했어요.</InlineNotice>
    : loading ? undefined
      : shownCount === 0 && query.trim() ? <Typography variant="body2" color="text.secondary">검색 결과가 없어요. 다른 검색어를 입력해 주세요.</Typography> : undefined;

  return <GlobalSearch
    label="통합 검색" placeholder="사람, 조직, 업무 검색"
    open={open} onOpenChange={(value) => value ? setOpen(true) : close()}
    query={query} onQueryChange={(value) => { setQuery(value); setShowAllTasks(false); }}
    groups={groups.filter((group) => group.items.length > 0)}
    onSelect={select} enabled={enabled} loading={loading} status={status}
    liveMessage={loading ? '사람·조직 검색 준비 중' : error ? '사람·조직 조회 실패. 다시 시도할 수 있습니다.' : query.trim() ? `검색 결과 ${shownCount}개 표시` : '바로가기'}
  />;
}
